import { useQuery, type ApolloError } from '@apollo/client';
import { Button, Skeleton } from '@newfront-insurance/core-ui/v2';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import {
  analyticsErrorMessage,
  analyticsScopeInput,
  analyticsScopeKey,
  findAnalyticsScope,
  invalidatesAnalyticsScope,
  sortedAnalyticsScopes,
} from './analytics-access';
import { NovaAnalyticsKpiCards } from './nova-analytics-kpis';
import { AskNovaAnalytics, NovaAnalyticsReviewPreviews } from './nova-analytics-previews';
import { NovaAnalyticsScopeSelect } from './nova-analytics-scope-select';
import { EverySkillTable, UsageByPersonTable, InactivePeopleTable } from './nova-analytics-tables';
import { useAnalyticsDiscovery } from './use-analytics-discovery';
import { analyticsQueryContext, useAnalyticsSection, type AnalyticsSectionProps } from './use-analytics-section';
import {
  GET_NOVA_ANALYTICS_DASHBOARD,
  GET_NOVA_ANALYTICS_KPIS,
  NOVA_ANALYTICS_INITIAL_PAGE,
  type NovaAnalyticsScope,
  type NovaAnalyticsScopeInput,
} from '../../../../queries/nova-analytics';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { useCanViewNovaAnalytics } from '../../hooks/use-can-view-nova-analytics';
import { useNovaNavigationSafe } from '../../navigation/nova-navigation-context';

function AnalyticsNotice({
  testId,
  message,
  onRetry,
  retryLabel = 'Retry dashboard',
}: {
  testId: string;
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
}) {
  return (
    <div
      data-testid={testId}
      role="alert"
      className="border-steel-200/50 text-steel-400 flex flex-col items-start gap-3 rounded-sm border p-4 text-sm"
    >
      <p>{message}</p>
      {onRetry ? (
        <Button data-testid={`${testId}-retry`} variant="secondary" size="small" onClick={onRetry}>
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}

function NovaAnalyticsMetrics({ scope, ...section }: AnalyticsSectionProps & { scope: NovaAnalyticsScopeInput }) {
  const {
    page: kpis,
    loading,
    error,
    retry,
  } = useAnalyticsSection(GET_NOVA_ANALYTICS_KPIS, { scope }, 'getNovaAnalyticsKpis', false, section);
  if (loading)
    return (
      <div role="status" aria-label="Loading usage metrics" className="grid grid-cols-1 gap-3 @min-[600px]:grid-cols-2">
        {[0, 1, 2, 3].map((index) => (
          <Skeleton key={index} className="h-[93px] rounded-sm" />
        ))}
      </div>
    );
  if (error || !kpis)
    return (
      <AnalyticsNotice
        testId="nova-analytics-kpis-error"
        message={analyticsErrorMessage(error) ?? 'Usage metrics couldn’t be loaded.'}
        onRetry={retry}
        retryLabel="Retry usage metrics"
      />
    );
  return (
    <>
      <div className="text-steel-400 flex flex-wrap items-center gap-2 text-xs leading-4">
        {kpis.isPreview ? <span className="bg-steel-50 rounded-sm px-2 py-1 font-semibold">Sample data</span> : null}
        <span>
          As of <time dateTime={kpis.asOfDate}>{kpis.asOfDate}</time>
        </span>
      </div>
      <NovaAnalyticsKpiCards kpis={kpis} />
    </>
  );
}

interface NovaAnalyticsSettingsProps {
  scope?: Partial<NovaAnalyticsScopeInput>;
  onScopeChange?: (scope?: NovaAnalyticsScopeInput) => void;
}

export function NovaAnalyticsSettings(props: NovaAnalyticsSettingsProps = {}) {
  const canViewAnalytics = useCanViewNovaAnalytics();
  const { userUuid, userScopes } = useChatOverlayContext();
  // A viewer/role change remounts every discovery, selection and in-flight query subscription.
  return canViewAnalytics && userUuid ? (
    <NovaAnalyticsDashboard key={JSON.stringify([userUuid, [...(userScopes ?? [])].sort()])} {...props} />
  ) : null;
}

function NovaAnalyticsDashboard({ scope: controlledScope, onScopeChange }: NovaAnalyticsSettingsProps) {
  const navigation = useNovaNavigationSafe();
  const view = navigation?.view;
  const routeScope =
    controlledScope ??
    (view?.kind === 'settings' &&
    view.section === 'analytics' &&
    (view.organizationId !== undefined || view.teamId !== undefined)
      ? { organizationId: view.organizationId, teamId: view.teamId }
      : undefined);
  const routeKey = JSON.stringify(routeScope ?? null);
  const [previousRoute, setPreviousRoute] = useState(routeKey);
  // undefined permits initial selection; null deliberately requires the user to choose after loss of access.
  const [selection, setSelection] = useState<Partial<NovaAnalyticsScopeInput> | null | undefined>(routeScope);
  const [accessError, setAccessError] = useState<ApolloError>();
  const [lostSelection, setLostSelection] = useState(false);
  const discovery = useAnalyticsDiscovery();
  const scopes = sortedAnalyticsScopes(discovery.data?.scopes ?? []);
  const selected =
    discovery.loading || discovery.error
      ? undefined
      : selection === undefined
        ? scopes[0]
        : selection
          ? findAnalyticsScope(scopes, selection)
          : undefined;
  // Reconcile controlled navigation before committing children, so the old scope cannot flash or fetch.
  if (previousRoute !== routeKey) {
    setPreviousRoute(routeKey);
    setSelection(routeScope ?? null);
    if (routeScope) {
      setAccessError(undefined);
      setLostSelection(false);
    }
  }
  if (!discovery.loading && !discovery.error && selection === undefined) {
    setSelection(selected ? analyticsScopeInput(selected) : null);
  }
  const { refresh } = discovery;
  const setRoute = useCallback(
    (next?: NovaAnalyticsScopeInput) => {
      onScopeChange?.(next);
      if (!onScopeChange) navigation?.actions.setAnalyticsScope(next);
    },
    [navigation?.actions, onScopeChange],
  );
  const select = (next: NovaAnalyticsScope) => {
    const input = analyticsScopeInput(next);
    setAccessError(undefined);
    setLostSelection(false);
    setSelection(input);
    setRoute(input);
  };
  const invalidate = useCallback(
    (error: ApolloError) => {
      setAccessError(error);
      setSelection(null);
      setRoute();
      refresh();
    },
    [refresh, setRoute],
  );
  if (invalidatesAnalyticsScope(discovery.error) && selection !== null) {
    setSelection(null);
    setAccessError(discovery.error);
    setLostSelection(true);
  }
  const missingSelection = !discovery.loading && !discovery.error && Boolean(selection) && !selected;
  if (missingSelection) {
    setSelection(null);
    setLostSelection(true);
  }
  useEffect(() => {
    if (lostSelection) setRoute();
  }, [lostSelection, setRoute]);

  return (
    <section
      data-testid="nova-analytics-dashboard"
      className="@container flex w-full flex-col gap-6"
      aria-labelledby="nova-analytics-heading"
    >
      <header className="flex flex-wrap items-start justify-between gap-6">
        <div className="flex flex-col gap-1">
          <h2 id="nova-analytics-heading" className="text-steel-500 text-lg leading-6 font-normal">
            Dashboard
          </h2>
          <p className="text-steel-400 text-sm leading-4">Who is using Nova, and which skills they are using.</p>
        </div>
        <div className="flex max-w-full flex-wrap items-center gap-2">
          <NovaAnalyticsScopeSelect
            scopes={scopes}
            selected={selected}
            disabled={discovery.refreshing || Boolean(discovery.error)}
            onChange={select}
          />
          <Button
            data-testid="nova-analytics-refresh"
            variant="secondary"
            size="small"
            disabled={discovery.refreshing}
            onClick={discovery.refresh}
          >
            Refresh analytics
          </Button>
        </div>
      </header>
      {discovery.refreshing && !discovery.loading ? (
        <p role="status" className="text-steel-400 text-sm">
          Checking reporting access…
        </p>
      ) : null}
      {discovery.loading ? (
        <div role="status" aria-label="Loading reporting access">
          <Skeleton className="h-24 w-full" />
        </div>
      ) : discovery.error || !discovery.data ? (
        <AnalyticsNotice
          testId="nova-analytics-discovery-error"
          message={analyticsErrorMessage(discovery.error) ?? 'Reporting access couldn’t be loaded.'}
          onRetry={discovery.refresh}
          retryLabel="Retry reporting access"
        />
      ) : scopes.length === 0 ? (
        <p data-testid="nova-analytics-no-access" role="status">
          You don’t have reporting access to any organizations or teams.
        </p>
      ) : !selected ? (
        <AnalyticsNotice
          testId="nova-analytics-no-selection"
          message={analyticsErrorMessage(accessError) ?? 'Choose an available organization or team to view analytics.'}
        />
      ) : (
        <ScopedAnalytics
          key={JSON.stringify([
            analyticsScopeKey(selected),
            discovery.data.permissionsFingerprint,
            discovery.generation,
          ])}
          scope={analyticsScopeInput(selected)}
          onInvalidScope={invalidate}
          refresh={discovery.refresh}
        />
      )}
    </section>
  );
}

function ScopedAnalytics({
  scope,
  onInvalidScope,
  refresh,
}: {
  scope: NovaAnalyticsScopeInput;
  onInvalidScope: (error: ApolloError) => void;
  refresh: () => void;
}) {
  const { data, loading, error, refetch } = useQuery(GET_NOVA_ANALYTICS_DASHBOARD, {
    variables: { scope, page: NOVA_ANALYTICS_INITIAL_PAGE },
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    context: analyticsQueryContext,
  });
  const invalid = invalidatesAnalyticsScope(error);
  useLayoutEffect(() => {
    if (invalid && error) onInvalidScope(error);
  }, [invalid, error, onInvalidScope]);
  const sourceDates = [
    data?.getNovaAnalyticsKpis?.asOfDate,
    data?.getNovaSkillUsage?.asOfDate,
    data?.getNovaPersonUsage?.asOfDate,
    data?.getNovaInactivePeople?.asOfDate,
  ].filter((date): date is string => Boolean(date));
  const sourceDate = sourceDates[0];
  const inconsistentDates = new Set(sourceDates).size > 1;
  const dateRetryStarted = useRef(false);
  const [dateRetryFinished, setDateRetryFinished] = useState(false);
  useEffect(() => {
    if (invalid || loading || !inconsistentDates || dateRetryStarted.current) return;
    dateRetryStarted.current = true;
    // Retry within this scope once. Refreshing discovery would remount us and reset the retry budget.
    void refetch()
      .catch(() => undefined)
      .finally(() => setDateRetryFinished(true));
  }, [invalid, loading, inconsistentDates, refetch]);
  const onSourceDateChange = useCallback(
    (date: string) => {
      if (!inconsistentDates && sourceDate && date !== sourceDate) refresh();
    },
    [sourceDate, inconsistentDates, refresh],
  );
  if (invalid) return null;
  const hasSectionData = Boolean(
    data?.getNovaAnalyticsKpis || data?.getNovaSkillUsage || data?.getNovaPersonUsage || data?.getNovaInactivePeople,
  );
  if (!loading && (error?.networkError || !hasSectionData))
    return (
      <AnalyticsNotice
        testId="nova-analytics-dashboard-error"
        message={analyticsErrorMessage(error) ?? 'Dashboard couldn’t be loaded.'}
        onRetry={() => {
          void refetch().catch(() => undefined);
        }}
      />
    );
  if (inconsistentDates) {
    if (loading || !dateRetryFinished)
      return (
        <div role="status" aria-label="Refreshing reporting dates">
          <Skeleton className="h-24 w-full" />
        </div>
      );
    return (
      <AnalyticsNotice
        testId="nova-analytics-inconsistent-dates"
        message="Analytics sections have different reporting dates. Refresh the dashboard to try again."
        onRetry={refresh}
      />
    );
  }
  const section = { initial: { data, loading, error }, onInvalidScope, onSourceDateChange };
  return (
    <>
      <NovaAnalyticsMetrics scope={scope} {...section} />
      <AskNovaAnalytics />
      {!loading && data?.getNovaAnalyticsKpis?.isPreview ? (
        <NovaAnalyticsReviewPreviews teamId={scope.teamId ?? null} />
      ) : null}
      <div key={sourceDate ?? 'loading'} className="flex flex-col gap-6">
        <EverySkillTable scope={scope} {...section} />
        <UsageByPersonTable scope={scope} {...section} />
        <InactivePeopleTable scope={scope} {...section} />
      </div>
    </>
  );
}
